/**
 * Infrastructure Layer - Web Camera Adapter
 * 
 * Implementación del escáner de cámara para plataforma web (PWA)
 * usando html5-qrcode.
 */

import {Html5Qrcode} from 'html5-qrcode';
import {
  ICameraScanner,
  CameraPermissionStatus,
  ScanResult,
} from '../../../domain/camera/interfaces/ICameraScanner';

export class WebCameraAdapter implements ICameraScanner {
  private html5QrCode: Html5Qrcode | null = null;
  private scanning: boolean = false;
  private readonly elementId: string = 'qr-reader';

  async requestPermissions(): Promise<CameraPermissionStatus> {
    try {
      // En web, solicitar permisos de cámara
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {facingMode: 'environment'},
      });
      
      // Detener el stream inmediatamente, solo queríamos verificar permisos
      stream.getTracks().forEach(track => track.stop());
      
      return {granted: true};
    } catch (error) {
      console.error('Error al solicitar permisos de cámara:', error);
      return {granted: false, canAskAgain: true};
    }
  }

  async hasPermission(): Promise<boolean> {
    try {
      // En web, verificar si tenemos acceso a la cámara
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      return videoDevices.length > 0;
    } catch (error) {
      console.error('Error al verificar permisos:', error);
      return false;
    }
  }

  async startScanning(
    onScan: (result: ScanResult) => void,
    onError?: (error: Error) => void
  ): Promise<void> {
    try {
      if (this.scanning) {
        console.warn('El escáner ya está activo');
        return;
      }

      // Verificar que el elemento existe en el DOM
      const element = document.getElementById(this.elementId);
      if (!element) {
        throw new Error(`Elemento con id "${this.elementId}" no encontrado en el DOM`);
      }

      this.html5QrCode = new Html5Qrcode(this.elementId);

      const config = {
        fps: 10,
        qrbox: {width: 250, height: 250},
        aspectRatio: 1.0,
      };

      await this.html5QrCode.start(
        {facingMode: 'environment'},
        config,
        (decodedText, decodedResult) => {
          onScan({
            data: decodedText,
            type: decodedResult.result.format?.formatName || 'QR_CODE',
          });
        },
        (errorMessage) => {
          // Ignorar errores de escaneo continuo (no se encontró QR)
          // Solo loguear para debugging
          // console.debug('Escaneo en progreso:', errorMessage);
        }
      );

      this.scanning = true;
    } catch (error: any) {
      console.error('Error al iniciar escaneo:', error);
      
      // Mejorar mensajes de error
      let errorMessage = 'Error al acceder a la cámara';
      
      if (error.name === 'NotReadableError') {
        errorMessage = 'La cámara está siendo usada por otra aplicación. Cierra otras pestañas o aplicaciones que usen la cámara e intenta de nuevo.';
      } else if (error.name === 'NotAllowedError') {
        errorMessage = 'Permisos de cámara denegados. Por favor, permite el acceso a la cámara en la configuración del navegador.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No se encontró ninguna cámara en este dispositivo.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      const finalError = new Error(errorMessage);
      
      if (onError) {
        onError(finalError);
      }
      throw finalError;
    }
  }

  async stopScanning(): Promise<void> {
    try {
      if (this.html5QrCode && this.scanning) {
        await this.html5QrCode.stop();
        this.html5QrCode.clear();
        this.html5QrCode = null;
        this.scanning = false;
      }
    } catch (error) {
      console.error('Error al detener escaneo:', error);
      this.scanning = false;
    }
  }

  isScanning(): boolean {
    return this.scanning;
  }
}
